/**
 * Record payment (design F, 600 px). One idempotency key per opening, so a
 * double click or a retry after a timeout never records the payment twice;
 * the server writes the key only on success, so fixing a rejected amount and
 * submitting again is still accepted.
 */
import React, { useState } from 'react';
import InvoiceRepository, { InvoiceDoc, Payment, PaymentMethod } from '../../../internals/repository/InvoiceRepository';
import { METHOD_LABEL, money, moneyInput, parseMoney, todayColombo } from '../format';
import { errorText } from '../detailModel';
import { Dialog, ErrorBanner } from './VoidModal';

const METHODS: PaymentMethod[] = ['CASH', 'BANK_TRANSFER', 'CHEQUE', 'CARD', 'LANKAQR', 'OTHER'];
const newKey = () => `pay-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const labelCls = 'flex flex-col gap-1.5 text-[13px] font-semibold text-text2';
const errCls = 'text-[12px] font-medium';

export interface RecordedPayment { payment: Payment; document: InvoiceDoc; receipt: InvoiceDoc }

export default function RecordPaymentModal({
  shopId, doc, onClose, onDone,
}: {
  shopId: string;
  doc: InvoiceDoc;
  onClose: () => void;
  onDone: (res: RecordedPayment) => void;
}) {
  const cur = doc.currency;
  const balance = Math.max(0, doc.balanceMinor);
  const today = todayColombo();
  const [key] = useState(newKey);
  const [amount, setAmount] = useState(moneyInput(balance));
  const [paidOn, setPaidOn] = useState(today);
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [chequeNo, setChequeNo] = useState('');
  const [chequeBank, setChequeBank] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErr, setFieldErr] = useState<Record<string, string>>({});

  const minor = parseMoney(amount);
  const amountErr = !(minor > 0)
    ? 'Enter an amount.'
    : minor > balance ? `This is more than the balance of ${money(balance, cur)}.` : fieldErr.amountMinor;
  const dateErr = !paidOn ? 'Enter the date paid.' : paidOn > today ? 'The date paid cannot be in the future.' : fieldErr.paidOn;
  const chequeErr = method === 'CHEQUE' && !chequeNo.trim() ? 'Enter the cheque number.' : fieldErr.chequeNo;
  const partial = minor > 0 && minor < balance;

  const submit = async () => {
    setTouched(true);
    if (busy || amountErr || dateErr || chequeErr) return;
    setBusy(true);
    setError(null);
    try {
      const res = await InvoiceRepository.recordPayment(shopId, doc.docId, {
        amountMinor: minor,
        paidOn,
        method,
        reference: reference.trim() || undefined,
        chequeNo: method === 'CHEQUE' ? chequeNo.trim() : undefined,
        chequeBank: method === 'CHEQUE' ? chequeBank.trim() || undefined : undefined,
        note: note.trim() || undefined,
      }, key);
      onDone(res);
    } catch (e: any) {
      const f = e?.fields || {};
      setFieldErr({
        ...(f.amountMinor || e?.code === 'amount_exceeds_balance' ? { amountMinor: 'This is more than the balance due.' } : {}),
        ...(f.paidOn || e?.code === 'invalid_paid_on' ? { paidOn: 'Use a date that is not in the future and not more than 60 days before the issue date.' } : {}),
        ...(f.chequeNo ? { chequeNo: 'Enter the cheque number.' } : {}),
      });
      setError(errorText(e, {
        forbidden_role: 'Only the owner or an admin can record non-cash payments.',
        invalid_state: 'This invoice can no longer take payments. Reload to see its current state.',
      }));
      setBusy(false);
    }
  };

  const fieldStyle = (bad?: string) => (touched && bad ? { borderColor: 'var(--neg)' } : undefined);
  const showErr = (bad?: string) => touched && bad
    ? <span className={errCls} style={{ color: 'var(--negFg)' }}>! {bad}</span>
    : null;

  return (
    <Dialog
      width={600}
      title="Record payment"
      sub={`${doc.number ?? 'Invoice'} · ${doc.customer?.companyName || doc.customer?.name || 'Customer'} · balance ${money(balance, cur)}`}
      onClose={onClose}
      busy={busy}
      footer={(
        <>
          <button type="button" className="nl-btn nl-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="nl-btn nl-btn--primary" onClick={submit} disabled={busy}>
            {busy ? 'Recording…' : `Record ${money(minor > 0 ? minor : 0, cur)}`}
          </button>
        </>
      )}
    >
      <form className="grid grid-cols-1 gap-3.5 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); submit(); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') { e.preventDefault(); submit(); } }}
        noValidate
      >
        <label className={labelCls}>
          Amount
          <input
            className="nl-input nl-num"
            inputMode="decimal"
            value={amount}
            aria-invalid={touched && !!amountErr}
            style={fieldStyle(amountErr)}
            onChange={(e) => { setAmount(e.target.value); setFieldErr((f) => ({ ...f, amountMinor: '' })); }}
          />
          {showErr(amountErr)}
          {!amountErr && partial && (
            <span className="text-[12px] font-medium text-text3">
              This is a partial payment. {money(balance - minor, cur)} will stay due.
            </span>
          )}
        </label>
        <label className={labelCls}>
          Date paid
          <input
            type="date"
            className="nl-input"
            value={paidOn}
            max={today}
            aria-invalid={touched && !!dateErr}
            style={fieldStyle(dateErr)}
            onChange={(e) => { setPaidOn(e.target.value); setFieldErr((f) => ({ ...f, paidOn: '' })); }}
          />
          {showErr(dateErr)}
        </label>

        <div className={`${labelCls} sm:col-span-2`} role="group" aria-label="Method">
          Method
          <div className="flex flex-wrap gap-1.5">
            {METHODS.map((m) => {
              const on = m === method;
              return (
                <button
                  key={m}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setMethod(m)}
                  className="nl-btn nl-btn--sm"
                  style={{
                    borderColor: on ? 'var(--brand)' : 'var(--border2)',
                    background: on ? 'var(--brandSoft)' : 'var(--surface)',
                    color: on ? 'var(--brand)' : 'var(--text)',
                  }}
                >
                  {METHOD_LABEL[m]}
                </button>
              );
            })}
          </div>
        </div>

        {method === 'CHEQUE' && (
          <>
            <label className={labelCls}>
              Cheque number
              <input
                className="nl-input nl-num"
                value={chequeNo}
                placeholder="e.g. 004512"
                aria-invalid={touched && !!chequeErr}
                style={fieldStyle(chequeErr)}
                onChange={(e) => { setChequeNo(e.target.value); setFieldErr((f) => ({ ...f, chequeNo: '' })); }}
              />
              {showErr(chequeErr)}
            </label>
            <label className={labelCls}>
              Bank
              <input className="nl-input" value={chequeBank} placeholder="e.g. HNB, Nugegoda" onChange={(e) => setChequeBank(e.target.value)} />
            </label>
          </>
        )}

        <label className={labelCls}>
          Reference
          <input className="nl-input" value={reference} placeholder="Optional" maxLength={120} onChange={(e) => setReference(e.target.value)} />
        </label>
        <label className={labelCls}>
          Note
          <input className="nl-input" value={note} placeholder="Optional" maxLength={280} onChange={(e) => setNote(e.target.value)} />
        </label>

        <div className="flex flex-col gap-1 rounded-[10px] border border-borderc bg-surface2 px-3.5 py-3 sm:col-span-2">
          <button type="button" role="switch" aria-checked={false} disabled className="flex cursor-not-allowed items-center gap-2.5 border-0 bg-transparent p-0 text-left text-[14px] font-semibold text-text3">
            <span className="relative shrink-0 rounded-full" style={{ width: 36, height: 20, background: 'var(--border2)' }}>
              <span className="absolute rounded-full bg-white" style={{ top: 2, left: 2, width: 16, height: 16 }} />
            </span>
            Send receipt to the customer
          </button>
          <span className="pl-[46px] text-[12px] text-text3">Sending arrives in a later release.</span>
        </div>

        {error && <div className="sm:col-span-2"><ErrorBanner text={error} /></div>}
      </form>
    </Dialog>
  );
}
